var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { assertDefined, compareArrays, convertLength, count, Observable, someMatch, toLengthUnit, } from "@configura/web-utilities";
import { CfgProduct } from "../CfgProduct.js";
import { CfgMtrlApplication } from "../material/CfgMtrlApplication.js";
import { CfgMtrlApplicationSource } from "../material/CfgMtrlApplicationSource.js";
import { wrapWithCache } from "../productLoader.js";
import { CfgOption, ProductConfigurationBubbleMode } from "./CfgOption.js";
import { _CfgProductConfigurationInternal } from "./CfgProductConfiguration.js";
import { getMtrlPreview } from "./utilitiesProductConfiguration.js";
export var SelectionType;
(function (SelectionType) {
    /**
     * All options are permanently selected. In our ui-component for this we skip over this level,
     * and immediately show the children, but you could do this differently, like for instance
     * showing the group heading.
     */
    SelectionType[SelectionType["Group"] = 0] = "Group";
    /**
     * One and only one can be selected at a time. This normally corresponds to either a dropdown
     * menu or radio buttons.
     */
    SelectionType[SelectionType["SelectOne"] = 1] = "SelectOne";
    /** Zero to all can be selected at a time. This normally corresponds to checkboxes. */
    SelectionType[SelectionType["SelectMany"] = 2] = "SelectMany";
})(SelectionType || (SelectionType = {}));
function getOptionFilter(option1) {
    return (option2) => option2._internal === option1;
}
/** Returns a fresh reference for the option at the given index */
function doFreshRefOptionAtIndex(options, index, committed, beforeNotify) {
    const freshRef = CfgOption._makeNewRefFrom(options[index]._internal);
    options[index] = freshRef;
    if (beforeNotify) {
        beforeNotify(freshRef);
    }
    freshRef._internal.changeObservable.notifyAll({ freshRef, committed });
    return freshRef;
}
/**
 * Returns a fresh reference for the given option.
 * @throws Throws an error if the option is not found in options.
 */
function doFreshRefOption(options, optionInternal, committed, beforeNotify) {
    const i = options.findIndex(getOptionFilter(optionInternal));
    if (i === -1) {
        throw new Error("Did not find the option in options");
    }
    return doFreshRefOptionAtIndex(options, i, committed, beforeNotify);
}
/**
 * This class is meant to only be used through CfgFeature. It should
 * never be instantiated on its own. Normally the internal state of this class
 * should never be directly modified. CfgFeature is the class that
 * should be used and interacted with.
 */
export class _CfgFeatureInternal {
    constructor(rawFeature, rawFeatures, _key, // Unique amongst siblings
    parent, parentConfiguration, parentProduct, rootProduct) {
        this.rawFeature = rawFeature;
        this.rawFeatures = rawFeatures;
        this._key = _key;
        this.parent = parent;
        this.parentConfiguration = parentConfiguration;
        this.parentProduct = parentProduct;
        this.rootProduct = rootProduct;
        this._selectedOptions = [];
        this._constrainedOptions = [];
        this.changeObservable = new Observable();
        this.setNumericValue = (val) => __awaiter(this, void 0, void 0, function* () {
            if (this.selectionType !== SelectionType.SelectOne) {
                throw new Error("Only SelectOne Features can have numeric values");
            }
            const option = this.options.find((option) => option.isAllowedNumericValue(val));
            if (option === undefined) {
                throw new Error("No Option allows the value you tried to set");
            }
            return yield option.setNumericValue(val, true);
        });
        this._notifyAllOfChange = (bubbleMode, committed) => __awaiter(this, void 0, void 0, function* () {
            if (bubbleMode === ProductConfigurationBubbleMode.Stop) {
                return;
            }
            const freshRef = CfgFeature._makeNewRefFrom(this);
            this.changeObservable.notifyAll({ freshRef, committed });
            const parent = this.parent;
            if (parent !== undefined) {
                yield parent._childHasChanged(freshRef, bubbleMode === ProductConfigurationBubbleMode.OneLevel
                    ? ProductConfigurationBubbleMode.Stop
                    : bubbleMode, committed);
            }
        });
        /**
         * Called by child to tell its parent that it has changed.
         * @throws Will throw if options have not yet been generated. This should be impossible
         * as nonexisting children can not call their parent.
         */
        this._childHasChanged = (childOption, bubbleMode, committed) => __awaiter(this, void 0, void 0, function* () {
            const options = this._options;
            if (options === undefined) {
                throw Error("Child says it changed, but no children has actually been generated");
            }
            const freshRef = doFreshRefOption(options, childOption, committed);
            const selectedOptions = this._selectedOptions;
            const i = selectedOptions.findIndex(getOptionFilter(childOption));
            if (i !== -1) {
                selectedOptions[i] = freshRef;
            }
            yield this._notifyAllOfChange(bubbleMode, committed);
        });
        // Keep in sync with stripExtendedDataFromDtoFeatureConf
        this.getDtoConf = (includeExtendedData) => {
            const result = {
                code: this.code,
            };
            if (includeExtendedData) {
                result.groupCode = this.groupCode;
                result.unit = this.unit;
            }
            const selectedOptions = this._selectedOptions;
            if (0 < selectedOptions.length) {
                result.options = selectedOptions.map((o) => o._internal.getDtoConf(includeExtendedData));
            }
            return result;
        };
        // DtoFeatureConf is the newer more easily readable format for configuration. As
        // the API:s are still using the older format (and will for the forseeable future) we need
        // to support both formats. The new format can be converted to the old, but not the other
        // way around. For that reason the get-method above uses the new format, and the set-method
        // below the old format. As these functions are meant to only be used internally this should't
        // cause too much confusion.
        this.setApiSelection = (apiOptionSelectionMap, apiOptionConstraintMap) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const selectionType = this.selectionType;
            const isGroup = selectionType === SelectionType.Group;
            const isSelectOne = selectionType === SelectionType.SelectOne;
            const isAllOptionsAffectedByAnySelection = this.isAllOptionsAffectedByAnySelection;
            const committed = true;
            let options;
            if (apiOptionSelectionMap === undefined) {
                options = this._options || []; // Already generated children (all or none)
                apiOptionSelectionMap = {};
            }
            else {
                options = this.options; // This will generate all children
            }
            let change = false;
            const constraintMap = apiOptionConstraintMap !== null && apiOptionConstraintMap !== void 0 ? apiOptionConstraintMap : {};
            // The first value that belongs to this features options is used as the key to look up constrained options
            const featureConstrainedOptions = (_b = (_a = options.map((o) => constraintMap[o.code]).find((o) => !!o)) === null || _a === void 0 ? void 0 : _a.constrOptions) !== null && _b !== void 0 ? _b : [];
            for (let i = 0; i < options.length; i++) {
                const option = options[i];
                const apiOptionSelection = apiOptionSelectionMap[option.code];
                const isConstrained = featureConstrainedOptions.indexOf(option.code) >= 0;
                const selectedIndex = this._selectedOptions.findIndex(getOptionFilter(option._internal));
                const constrainedIndex = this._constrainedOptions.findIndex(getOptionFilter(option._internal));
                const isOn = isGroup || apiOptionSelection;
                if (isConstrained) {
                    if (constrainedIndex === -1) {
                        doFreshRefOptionAtIndex(options, i, committed, (freshRef) => this._constrainedOptions.push(freshRef));
                        change = true;
                    }
                }
                else {
                    if (constrainedIndex !== -1) {
                        this._constrainedOptions.splice(constrainedIndex, 1);
                        doFreshRefOptionAtIndex(options, i, committed);
                        change = true;
                    }
                }
                if (isOn) {
                    if (isSelectOne) {
                        if (this.isUseNumericValue) {
                            const { numericValue } = apiOptionSelection;
                            if (numericValue === undefined) {
                                throw new Error("No numeric value for numeric feature");
                            }
                            const { value, unit } = numericValue;
                            if (yield option.setNumericValue(unit === undefined
                                ? value
                                : convertLength(value, toLengthUnit(unit), this.unit), false)) {
                                change = true;
                            }
                        }
                    }
                    if (selectedIndex === -1) {
                        if (isAllOptionsAffectedByAnySelection) {
                            this._selectedOptions.push(option);
                        }
                        else {
                            doFreshRefOptionAtIndex(options, i, committed, (freshRef) => this._selectedOptions.push(freshRef));
                        }
                        change = true;
                    }
                }
                else {
                    if (selectedIndex !== -1) {
                        this._selectedOptions.splice(selectedIndex, 1);
                        if (!isAllOptionsAffectedByAnySelection) {
                            doFreshRefOptionAtIndex(options, i, committed);
                        }
                        change = true;
                    }
                }
                if (yield option._internal.setApiSelection(apiOptionSelection, constraintMap[option.code])) {
                    change = true;
                }
            }
            if (this.ancestorsSelected) {
                const selectionCount = this._selectedOptions.length;
                if (isSelectOne && selectionCount !== 1) {
                    const wrongCountWarning = `A select-one Feature (i.e. neither optional nor multiple) should have exactly one Option selected. Feature key: "${this.key}". Actual: ${selectionCount}`;
                    if (this.rootProduct.settings.strictSelectOneSelectionCount) {
                        throw new Error(wrongCountWarning);
                    }
                    else {
                        console.warn(wrongCountWarning);
                    }
                }
            }
            if (change) {
                if (isAllOptionsAffectedByAnySelection) {
                    this._freshRefAllOptions(committed);
                }
                if (selectionType === SelectionType.SelectOne) {
                    yield this.pushStretch();
                }
                // setApiSelection works its way top down and handles notifications on
                // each level by looking at change returned from its children. That way
                // we do not get multiple notifications for the same ancestral node even
                // if multiple descendants are affected by the setApiSelection. Therefore
                // we use OneLevel to make this feature notify and the parent update its
                // reference.
                yield this._notifyAllOfChange(ProductConfigurationBubbleMode.OneLevel, committed);
            }
            return change;
        });
        this.addForApiConstrained = (next) => {
            const constrOptions = this._constrainedOptions;
            for (const so of this._selectedOptions) {
                next[so.code] = so._internal.getApiConstrained(constrOptions);
            }
        };
        /** Pushes to refresh stretch. Does not cause validation. */
        this.pushStretch = () => __awaiter(this, void 0, void 0, function* () {
            if (this.selectionType !== SelectionType.SelectOne) {
                throw new Error("Can only push stretch for SelectOne");
            }
            const value = this.numericValue;
            return (yield Promise.all((this.measureParamCodes || []).map((measureParamCode) => this.parentConfiguration.setStretchReferenceLength(measureParamCode, value, this.unit)))).some((change) => change);
        });
        this.structureCompare = (other, strictOrder = true, descriptionMatch = false) => this.keyMatch(other, descriptionMatch) &&
            compareArrays(this.options, other.options, (l, r) => l._internal.structureCompare(r._internal, strictOrder, descriptionMatch), strictOrder);
        this.tryMatchSelection = (other, descriptionMatch = false) => __awaiter(this, void 0, void 0, function* () {
            const { selectionType, isUseNumericValue } = this;
            if (selectionType !== other.selectionType) {
                // Will not try if the selection type is different
                return false;
            }
            if (isUseNumericValue !== other.isUseNumericValue) {
                return false;
            }
            let change = false;
            if (isUseNumericValue) {
                // For numeric options we do not try to go any further down. It's becoming to
                // strange to make informed guesses on how to progress down the tree.
                const otherNumericValue = other.numericValue;
                if (otherNumericValue === undefined) {
                    throw new Error("Numeric feature without numeric value, this should never happen");
                }
                else {
                    if (yield this.setNumericValue(otherNumericValue)) {
                        change = true;
                    }
                }
            }
            else {
                const thisOptions = this.options;
                const otherOptions = other.options;
                change = (yield Promise.all(otherOptions.map((otherO) => (() => __awaiter(this, void 0, void 0, function* () {
                    const otherOSelected = otherO.selected;
                    if (selectionType === SelectionType.SelectOne && !otherOSelected) {
                        return false;
                    }
                    if (1 <
                        count(otherOptions, (item) => item._internal.keyMatch(otherO._internal, descriptionMatch))) {
                        console.warn("tryMatchSelection will ignore options that have the same key");
                        return false;
                    }
                    const toTryChangeOptions = thisOptions.filter((o) => otherO._internal.keyMatch(o._internal, descriptionMatch));
                    if (1 < toTryChangeOptions.length) {
                        console.warn("tryMatchSelection will ignore options that have the same key");
                        return false;
                    }
                    if (toTryChangeOptions.length === 0) {
                        return false;
                    }
                    const toTryChangeOption = toTryChangeOptions[0];
                    let change = false;
                    if (selectionType === SelectionType.SelectMany ||
                        selectionType === SelectionType.SelectOne) {
                        // The setSelected will only affect ourselves,
                        // so we do not need to bubble the notification.
                        // Instead we use the change variable to know
                        // when to notify for this feature.
                        change = yield toTryChangeOption._internal.parent.selectOption(toTryChangeOption._internal, otherOSelected, ProductConfigurationBubbleMode.Stop);
                    }
                    if (otherOSelected &&
                        (yield toTryChangeOption._internal.tryMatchSelection(otherO, descriptionMatch))) {
                        change = true;
                    }
                    return change;
                }))()))).some((b) => b);
            }
            if (change) {
                // tryMatchSelection works its way top down and handles notifications on
                // each level by looking at change returned from its children. That way
                // we do not get multiple notifications for the same ancestral node even
                // if multiple descendants are affected by the tryMatchSelection. Therefore
                // we use OneLevel to make this feature notify and the parent update its
                // reference.
                yield this._notifyAllOfChange(ProductConfigurationBubbleMode.OneLevel, true);
            }
            return change;
        });
        /**
         * Normally this is used through methods on CfgFeature and CfgOption. Use this internal version
         * if you need to control the bubbleMode.
         *
         * Using a validate bubbleMode will cause validation calls to the server.
         */
        this.selectOption = (optionInternal, on, bubbleMode) => __awaiter(this, void 0, void 0, function* () {
            if (bubbleMode ===
                ProductConfigurationBubbleMode.ValidateAndBubbleSelectedAndApplySyncGroups) {
                const product = this.rootProduct;
                const syncGroupHandler = product.syncGroupHandler;
                assertDefined(syncGroupHandler, `Sync group handler is required for bubble mode ${ProductConfigurationBubbleMode.ValidateAndBubbleSelectedAndApplySyncGroups}`);
                return yield syncGroupHandler.selectOption(product, optionInternal, on, wrapWithCache(product._productLoaderRaw));
            }
            if (!on) {
                if (this.selectionType === SelectionType.Group) {
                    throw new Error(`Multiple features are always selected and are not user selectable. Feature key: "${this.key}".`);
                }
                if (this.selectionType === SelectionType.SelectOne) {
                    throw new Error(`Select one can never be deselected. Feature key: "${this.key}".`);
                }
            }
            const selectedOptions = this._selectedOptions;
            const isAllOptionsAffectedByAnySelection = this.isAllOptionsAffectedByAnySelection;
            const index = selectedOptions.findIndex(getOptionFilter(optionInternal));
            const isActualChange = (index === -1) === on;
            const committed = true;
            if (this.selectionType !== SelectionType.Group) {
                if (on) {
                    // Calling this.options will populate (generate) all Options if they have not yet
                    // been generated. As we are selecting an option we need all Options to be generated.
                    const options = this.options;
                    if (this.selectionType === SelectionType.SelectOne) {
                        let removeOption = selectedOptions.shift();
                        while (removeOption !== undefined) {
                            if (!isAllOptionsAffectedByAnySelection) {
                                doFreshRefOption(options, removeOption._internal, committed);
                            }
                            removeOption = selectedOptions.shift();
                        }
                    }
                    if (isAllOptionsAffectedByAnySelection) {
                        selectedOptions.push(CfgOption._makeNewRefFrom(optionInternal));
                    }
                    else {
                        doFreshRefOption(options, optionInternal, committed, (freshRef) => selectedOptions.push(freshRef));
                    }
                    if (this.selectionType === SelectionType.SelectOne) {
                        yield this.pushStretch();
                    }
                }
                else {
                    selectedOptions.splice(index, 1);
                    if (!isAllOptionsAffectedByAnySelection) {
                        // Accessing this._options gives us the Options that have been generated
                        // or undefined if the Options have not yet been generated. As this action
                        // is deselect and deselected is the default uninitialized state we can
                        // safely ignore if undefined.
                        const options = this._options;
                        if (options !== undefined) {
                            doFreshRefOption(options, optionInternal, committed);
                        }
                    }
                }
                if (isAllOptionsAffectedByAnySelection) {
                    this._freshRefAllOptions(committed);
                }
            }
            let nextLevelBubbleMode = bubbleMode;
            if (!on) {
                // If this was a deselect action we shall not bubble selected, that is, we shall not
                // select the ancestors if this action was deselect.
                if (bubbleMode === ProductConfigurationBubbleMode.ValidateAndBubbleSelected) {
                    nextLevelBubbleMode = ProductConfigurationBubbleMode.Validate;
                }
                else if (bubbleMode === ProductConfigurationBubbleMode.BubbleSelected) {
                    nextLevelBubbleMode = ProductConfigurationBubbleMode.ToRoot;
                }
            }
            yield this._notifyAllOfChange(nextLevelBubbleMode, committed);
            return isActualChange;
        });
        this.isSelected = (option) => this.selectionType === SelectionType.Group ||
            this._selectedOptions.some(getOptionFilter(option));
        this.isDisabled = (option) => this._constrainedOptions.some(getOptionFilter(option));
        this.keyMatch = (other, descriptionMatch = false) => descriptionMatch
            ? this.description.toLowerCase() === other.description.toLowerCase()
            : this.code === other.code;
        /** Only in selected options */
        this._getFeaturesWithCode = (code) => this._selectedOptions.reduce((agg, selectedOption) => {
            agg.push(...selectedOption._internal._getFeaturesWithCode(code));
            return agg;
        }, code === this.code ? [this] : []);
        if (rawFeature.multiple) {
            this.selectionType = SelectionType.Group;
        }
        else if (rawFeature.optional) {
            this.selectionType = SelectionType.SelectMany;
        }
        else {
            this.selectionType = SelectionType.SelectOne;
        }
        this.hasUpcharge = rawFeature.options.some((option) => (option.upcharge !== undefined && option.upcharge !== 0) ||
            (option.priceCodes !== undefined && option.priceCodes.length > 0));
    }
    get code() {
        return this.rawFeature.code;
    }
    get groupCode() {
        return this.rawFeature.groupCode;
    }
    get key() {
        return this._key;
    }
    set key(k) {
        this._key = k;
    }
    get notes() {
        var _a;
        return this.parentProduct.getNotes((_a = this.rawFeature.noteRefs) !== null && _a !== void 0 ? _a : []);
    }
    get isUseNumericValue() {
        return this.rawFeature.numericOrder;
    }
    get numericValue() {
        if (this.selectionType !== SelectionType.SelectOne) {
            throw new Error("Only SelectOne Features can have numeric values");
        }
        const { selectedOptions } = this;
        if (selectedOptions.length === 0) {
            return undefined;
        }
        const value = selectedOptions[0].numericValue;
        if (value === undefined) {
            if (this.isUseNumericValue) {
                throw new Error("No numericValue set on option with numeric sku feature");
            }
            return undefined;
        }
        return value;
    }
    get description() {
        return this.rawFeature.description;
    }
    get omitOnOrder() {
        return this.rawFeature.omitOnOrder === true;
    }
    get syncGroup() {
        return this.rawFeature.syncGroup;
    }
    /**
     * @return one of the following, in order:
     * - undefined if the Feature lacks a syncGroup.
     * - false if the syncGroup doesn't fulfill the optional mustSupport requirement.
     * - syncCode from the syncGroup.
     */
    getSyncCode(mustSupport) {
        if (this.syncGroup === undefined) {
            return undefined;
        }
        const { syncGroupCode, syncMethod } = this.syncGroup;
        if (mustSupport === undefined || syncMethod === "twoWay" || syncMethod === mustSupport) {
            return syncGroupCode;
        }
        return false;
    }
    /**
     * The DtoMeasureParam class is re-used for different purposes. In Features it is used
     * to indicate which stretch measures inside Models shall be affected by this state
     * of this Feature. Hence only the code property is used.
     */
    get measureParamCodes() {
        var _a;
        return (_a = this.rawFeature.measureParams) === null || _a === void 0 ? void 0 : _a.map((measureParam) => measureParam.code);
    }
    get unit() {
        const unit = this.rawFeature.unit;
        return unit === undefined ? this.parentProduct.unit : toLengthUnit(unit);
    }
    get mtrlApplications() {
        if (this._mtrlApplications === undefined) {
            this._mtrlApplications = (this.rawFeature.mtrlApplications || []).map((m) => CfgMtrlApplication.fromMtrlLikeApplication(CfgMtrlApplicationSource.Feature, m));
        }
        return this._mtrlApplications;
    }
    get selectedOptions() {
        return this._selectedOptions;
    }
    get ancestorsSelected() {
        return (this.parent instanceof _CfgProductConfigurationInternal || this.parent.ancestorsSelected);
    }
    /**
     * If one option is selected or deselected this will potentially
     * affect all other Options on this Feature
     */
    get isAllOptionsAffectedByAnySelection() {
        return this.selectionType === SelectionType.SelectOne && this.hasUpcharge !== false;
    }
    get preview() {
        return getMtrlPreview(this._mtrlApplications);
    }
    /**
     * Please note that this relates to the visibility in the Configuration tree.
     * It does not affect the visibility of anything in the 3D view at all.
     */
    get visibleIfAdditionalProduct() {
        return this.rawFeature.hideIfAdditionalProduct !== true;
    }
    /**
     * Please note that this relates to the visibility in the Configuration tree.
     * It does not affect the visibility of anything in the 3D view at all.
     */
    get visibleIfMainProduct() {
        return this.rawFeature.hideIfMainProduct !== true;
    }
    /**
     * Please note that this relates to the visibility in the Configuration tree.
     * It does not affect the visibility of anything in the 3D view at all.
     */
    get visible() {
        return this.parentProduct.isAdditionalProduct
            ? this.visibleIfAdditionalProduct
            : this.visibleIfMainProduct;
    }
    get options() {
        if (this._options === undefined) {
            const hasDuplicateDescription = this.parentProduct.settings.disableMatchOnDescription
                ? true
                : someMatch(this.rawFeature.options, (left, right) => left.description.toLowerCase() === right.description.toLowerCase());
            this._options = this.rawFeature.options.map((o) => CfgOption.make(o, this.rawFeatures, hasDuplicateDescription, this, this.parentConfiguration, this.parentProduct, this.rootProduct));
        }
        return this._options;
    }
    /**
     * Make fresh references to all options on this feature.
     * Also includes currently selected options.
     * @throws Will throw if options have not yet been generated.
     */
    _freshRefAllOptions(committed) {
        const options = this._options;
        const selectedOptions = this._selectedOptions;
        if (options === undefined) {
            throw new Error("We expect all options to be generated at freshref");
        }
        for (let i = 0; i < options.length; i++) {
            doFreshRefOptionAtIndex(options, i, committed, (freshRef) => {
                const selectedIndex = selectedOptions.findIndex(getOptionFilter(freshRef._internal));
                if (selectedIndex !== -1) {
                    selectedOptions[selectedIndex] = freshRef;
                }
            });
        }
    }
}
export class CfgFeature {
    /**
     * Private constructor and make-method because make new ref requires the constructor to
     * take an internal and we don't want those who instantiate CfgFeature to have to be aware
     * of the internal.
     */
    constructor(_internal) {
        this._internal = _internal;
        this.isBackedBySame = (other) => this._internal === other._internal;
        /**
         * This will find the first option allowing the value, set the value on it and select it.
         * This is an implicit option-select.
         */
        this.setNumericValue = (val) => __awaiter(this, void 0, void 0, function* () { return yield this._internal.setNumericValue(val); });
        /**
         * Selects the passed Option.
         * Only Options belonging to Features that are "select many" can be deselected.
         * Calling this will cause a validation call to the server.
         */
        this.selectOption = (option, on) => __awaiter(this, void 0, void 0, function* () {
            return yield this._internal.selectOption(option._internal, on, ProductConfigurationBubbleMode.ValidateAndBubbleSelectedAndApplySyncGroups);
        });
        this.isSelected = (option) => this._internal.isSelected(option._internal);
        this.listenForChange = (l) => this._internal.changeObservable.listen(l);
        this.stopListenForChange = (l) => this._internal.changeObservable.stopListen(l);
    }
    static make(rawFeature, rawFeatures, key, parent, parentConfiguration, parentProduct, rootProduct) {
        return new this(new _CfgFeatureInternal(rawFeature, rawFeatures, key, parent, parentConfiguration, parentProduct, rootProduct));
    }
    /**
     * Makes an object wrapping the passed object. This is not a clone method,
     * it is a method to make a new outer reference. Like a shallow copy.
     * We use this to help frameworks that are built around using equals to detect change.
     */
    static _makeNewRefFrom(internal) {
        return new this(internal);
    }
    get parentProduct() {
        return CfgProduct._makeNewRefFrom(this._internal.parentProduct);
    }
    get rootProduct() {
        return CfgProduct._makeNewRefFrom(this._internal.rootProduct);
    }
    get selectionType() {
        return this._internal.selectionType;
    }
    // Unique amongst siblings. Can change. Only use for presentation layer i.e. React.
    get key() {
        return this._internal.key;
    }
    get code() {
        return this._internal.code;
    }
    get groupCode() {
        return this._internal.groupCode;
    }
    get notes() {
        return this._internal.notes;
    }
    /**
     * If true the options in the feature is selected by both sending its code and numeric value
     * when selecting.
     */
    get isUseNumericValue() {
        return this._internal.isUseNumericValue;
    }
    /** This will read the numeric value of the selected option. */
    get numericValue() {
        return this._internal.numericValue;
    }
    get unit() {
        return this._internal.unit;
    }
    get description() {
        return this._internal.description;
    }
    get omitOnOrder() {
        return this._internal.omitOnOrder;
    }
    get hasUpcharge() {
        return this._internal.hasUpcharge;
    }
    get selectedOptions() {
        return this._internal.selectedOptions;
    }
    /** Are all ancestors up to the CfgProductConfiguration selected? */
    get ancestorsSelected() {
        return this._internal.ancestorsSelected;
    }
    get preview() {
        return this._internal.preview;
    }
    get options() {
        return this._internal.options;
    }
    /**
     * Please note that this relates to the visibility in the Configuration tree.
     * It does not affect the visibility of anything in the 3D view at all.
     * Visibility is not inherited. If this is hidden the children
     * of this Feature might still be visible, depending on their settings.
     */
    get visible() {
        return this._internal.visible;
    }
}
